import { API_BASE_URL } from '../config';
import { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import Confetti from 'react-confetti';
import PictureSelectionModal from './PictureSelectionModal';
import Picture from './Picture';
import ItemQuantity from './ItemQuantity';
import LoadingModal from './LoadingModal';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { useNavigate } from 'react-router-dom';
import AppContext from '../contexts/AppContext';
import { CheckCircledIcon, Cross2Icon, CameraIcon, ImageIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { appNavigate } from '../utils/appNavigation';
import { useCmdbChain } from '../hooks/useCmdbChain';
import InterventionPointsDetail from './InterventionPointsDetail';
import AttachmentsGallery from './AttachmentsGallery';
import { getErrorMessage } from '../constants/errorCodes';
import { getOptionImageUrl, useStockBadge } from '../utils/cmdbOptionAttributes';
import QuotaPanel from './QuotaPanel';
import UploadProgressIndicator from './UploadProgressIndicator';
import { hasRole } from '../utils/roleUtils';

const DEFAULT_MULTI_FIELDS = ['customfield_10102'];
const itemsWithPictures = ['customfield_10098', 'customfield_10102'];
const hasQuantity = ['customfield_10102'];
// Client-specific fields where the react-select is hidden: the user can ONLY
// pick assets through the picture selection modal (call-to-action button).
const MODAL_ONLY_FIELDS = ['customfield_10102'];
const fieldsWithAttributes = ['customfield_10628', 'customfield_10096']; // intervention points DPGP and LUXE

const resolveInterventionPointAttributes = (config, keys) => {
  return (keys || [])
    .map((key) => {
      if (!key || typeof key !== 'string') return key;
      const attribute = config?.attributes?.[key];
      if (!attribute) return null;

      return {
        key,
        label: attribute.label || attribute.name,
        ...attribute,
      };
    })
    .filter(Boolean);
};

// Add custom hook for window width
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

function FormType({ formTypeFields, selectedFormType, selectedFormTypeName, onCancel, userAccountId, userSectors, userRole, deviceIsInWebView = false, serviceDeskId = null, prefillInterventionPointKey = null, prefillFurnitureKey = null }) {
  const { t } = useTranslation();
  const { appendStockToOptionLabel } = useStockBadge();
  const [formData, setFormData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOptions, setModalOptions] = useState([]);
  const [currentField, setCurrentField] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState([]);
  const [error, setError] = useState(null);
  const [modalSelections, setModalSelections] = useState([]);
  const [picturesData, setPicturesData] = useState([]);
  const [mobilierIllustrations, setMobilierIllustrations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPrefilling, setIsPrefilling] = useState(false);
  const [openSelectId, setOpenSelectId] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [fieldsAttributes, setFieldsAttributes] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState(new Set());
  const [appliedPrefillKey, setAppliedPrefillKey] = useState(null);
  const prefillChainLoadedRef = useRef(null);
  const urlParams = new URLSearchParams(window.location.search);
  const withQR = urlParams.get('withQR') || false;
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { serviceDesk } = useContext(AppContext);
  const [serviceDeskConfig, setServiceDeskConfig] = useState(serviceDesk?.config);
  const hasQuota = Boolean(serviceDeskConfig?.hasQuota);
  const [quotaState, setQuotaState] = useState({ checking: false, blocking: false });

  useEffect(() => {
    const fetchServiceDeskConfig = async () => {
      if (!serviceDeskId) {
        setServiceDeskConfig(serviceDesk?.config);
        return;
      }

      try {
        const response = await fetchWithAuth(`service-desks/${serviceDeskId}/config`);
        const result = await response.json();
        if (result.success) {
          setServiceDeskConfig(result.data);
        }
      } catch (error) {
        console.error('Error fetching service desk config:', error);
        setServiceDeskConfig(undefined);
      }
    };

    fetchServiceDeskConfig();
  }, [serviceDeskId, serviceDesk?.config]);

  const cmdbFields = useMemo(() => {
    if (!formTypeFields || !Array.isArray(formTypeFields)) {
      return [];
    }
    return formTypeFields.filter(field => {
      return field.jiraSchema.custom === 'com.atlassian.jira.plugins.cmdb:cmdb-object-cftype' ||
      field.jiraSchema?.custom?.includes('elements-connect');
    }).map(field => field.fieldId);
  }, [formTypeFields]);

  const requiredFieldIds = useMemo(() => {
    return (formTypeFields || [])
      .filter(f => f?.required)
      .map(f => f.fieldId)
      .filter(Boolean);
  }, [formTypeFields]);

  const isMultiFields = useMemo(() => {
    if (!formTypeFields || !Array.isArray(formTypeFields)) {
      return DEFAULT_MULTI_FIELDS.concat(fieldsAttributes.multiFields || []);
    }
    const elementsConnectMultiFields = formTypeFields.filter(field => {
      return field.jiraSchema?.custom?.includes('elements-connect-multi-select-cf-object');
    }).map(field => field.fieldId);

    return DEFAULT_MULTI_FIELDS.concat(fieldsAttributes.multiFields || []).concat(elementsConnectMultiFields);
  }, [formTypeFields, fieldsAttributes.multiFields]);

  const ATTR_CONFIG = serviceDeskConfig?.interventionPointConfig || {};
  const detailAttributes = resolveInterventionPointAttributes(ATTR_CONFIG, ATTR_CONFIG.details);

  const getCurrentCmdbValue = useCallback((fieldId) => {
    const value = formData?.[fieldId];
    if (!value) return null;
    if (Array.isArray(value)) return value[0] || null;
    if (value && (value.value || value.label)) return value;
    return null;
  }, [formData]);

  const onSelectFilesChange = async (field, e) => {
    const MAX_FILES = 4;
    const currentFiles = formData[field.fieldId] || [];
    let newFiles = Array.from(e.target.files);
    const remainingSlots = MAX_FILES - currentFiles.length;

    // If user selected more files than remaining slots, only take the allowed number
    if (newFiles.length > remainingSlots) {
      newFiles = newFiles.slice(0, remainingSlots);
    }

    const files = newFiles.map(file => ({
      file,
      id: Math.random().toString(36).substring(2, 9),
      isUploading: true
    }));

    handleInputChange(field, [
      ...currentFiles,
      ...files
    ]);

    const uploadedFiles = [...(formData['_attachments'] || [])];

    setUploadingFiles(prev => new Set([
      ...prev,
      ...files.map(f => f.id)
    ]));

    for (const fileObj of files) {
      try {
        const uploadedFile = await uploadFile(fileObj.file, fileObj.id);
        uploadedFiles.push(uploadedFile);

        setFormData(prev => ({
          ...prev,
          [field.fieldId]: (prev[field.fieldId] || []).map(f =>
            f.id === fileObj.id ? { ...f, isUploading: false } : f
          )
        }));
      } catch (error) {
        setFormData(prev => ({
          ...prev,
          [field.fieldId]: (prev[field.fieldId] || []).map(f =>
            f.id === fileObj.id ? { ...f, isUploading: false, uploadFailed: true } : f
          )
        }));
      } finally {
        setUploadingFiles(prev => {
          const next = new Set(prev);
          next.delete(fileObj.id);
          return next;
        });
      }
    }

    // Update state once with all uploaded files
    handleInputChange({ fieldId: '_attachments' }, uploadedFiles);

    // Reset input pour permettre de resélectionner les mêmes fichiers
    e.target.value = '';
  };

  useEffect(() => {
    // foreach formTypeFields.defaultValues and set the formData
    formTypeFields.forEach(field => {
      if (field.defaultValues.length === 1)
        setFormData(prev => ({ ...prev, [field.fieldId]: { ...field.defaultValues[0], type: field?.jiraSchema?.type } }));
      else if (field.defaultValues.length > 1)
        setFormData(prev => ({ ...prev, [field.fieldId]: { ...field.defaultValues, type: field?.jiraSchema?.type } }));
    });
  }, []);

  useEffect(() => {
    if (!prefillInterventionPointKey || !formTypeFields?.length) return;

    const prefillKey = `${selectedFormType}:${serviceDeskId || 'default'}:${prefillInterventionPointKey}:${prefillFurnitureKey || ''}`;
    if (appliedPrefillKey === prefillKey) return;

    const validFieldsById = new Map(formTypeFields.map(field => [field.fieldId, field]));
    let cancelled = false;

    const loadPrefill = async () => {
      try {
        setIsPrefilling(true);
        const params = new URLSearchParams();

        if (Array.isArray(userSectors) && userSectors.length) params.append('userSectors', userSectors.join(','));
        if (userRole) params.append('userRole', userRole);
        if (userAccountId) params.append('userAccountId', userAccountId);
        if (serviceDeskId) params.append('serviceDeskId', serviceDeskId);
        if (prefillFurnitureKey) params.append('furniture', prefillFurnitureKey);

        const qs = params.toString();
        const response = await fetchWithAuth(`assets/intervention-point/${encodeURIComponent(prefillInterventionPointKey)}/prefill${qs ? `?${qs}` : ''}`);
        const prefill = await response.json();

        if (cancelled) return;

        setFormData(prev => {
          const next = { ...prev };

          Object.entries(prefill || {}).forEach(([fieldId, value]) => {
            const field = validFieldsById.get(fieldId);
            if (!field || next[fieldId] || !value) return;

            const option = {
              value: value.value,
              label: value.label,
              key: value.key || value.objectKey,
              type: field?.jiraSchema?.type,
            };

            next[fieldId] = isMultiFields.includes(fieldId) ? [option] : option;
          });

          return next;
        });
        setAppliedPrefillKey(prefillKey);
      } catch (error) {
        console.error('Error loading intervention point prefill:', error);
      } finally {
        if (!cancelled) setIsPrefilling(false);
      }
    };

    loadPrefill();

    return () => {
      cancelled = true;
    };
  }, [prefillInterventionPointKey, prefillFurnitureKey, formTypeFields, selectedFormType, serviceDeskId, userSectors, userRole, userAccountId, appliedPrefillKey, isMultiFields]);

  useEffect(() => {
    const loadPicturesData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL.replace('api', '')}pictures-data.json`);
        const data = await response.json();
        setPicturesData(data);
      } catch (error) {
        console.error('Error loading pictures data:', error);
      }
    };

    const loadMobilierIllustrations = async () => {
      try {
        // Use the files route with the appropriate secret
        const secret = 'm834dsfER@fgd'; // This should ideally come from environment variables
        const response = await fetchWithAuth(`files/mobilier_illustrations?secret=${secret}`);
        if (!response.ok) {
          throw new Error('Failed to load mobilier illustrations');
        }
        const data = await response.json();
        setMobilierIllustrations(data);
      } catch (error) {
        console.error('Error loading mobilier illustrations:', error);
      }
    };

    loadPicturesData();
    loadMobilierIllustrations();
  }, []);

  // CMDB chaining via hook
  const mapOptions = (payload) => {
    const values = payload?.values || payload?.data?.values || payload || [];
    const arr = Array.isArray(values) ? values : [];
    return arr.map(v => ({
      value: v.globalId || v.value || v.id || v.objectId || v.key,
      label: v.label || v.name || v.displayName || v.objectLabel || String(v.id || v.key || v.globalId),
      key: v.objectKey || v.key,
      attributes: v.attributes || []
    }));
  };

  // Memoized so useCmdbChain's prefetch effect doesn't re-fire (and burst requests)
  // on every render. Only changes when the actual user context changes.
  const cmdbUserCtx = useMemo(
    () => ({ sectors: userSectors || [], role: userRole || 'user', accountId: userAccountId || null }),
    [userSectors, userRole, userAccountId],
  );

  const cmdb = useCmdbChain({
    fieldsOrder: cmdbFields,
    getCurrentValue: getCurrentCmdbValue,
    setValue: (fieldId, option) => {
      const field = formTypeFields.find(f => f.fieldId === fieldId);
      const type = field?.jiraSchema?.type;
      const isMultiField = isMultiFields.includes(fieldId);

      // For CMDB multi-select fields, form state is controlled via handleInputChange
      // (which stores the full array). The chain only needs a representative value,
      // provided via getCurrentValue, so we avoid overwriting the array here.
      if (isMultiField) {
        // If option is null, clear the field value
        if (!option) {
          setFormData((prev) => {
            const copy = { ...prev };
            delete copy[String(fieldId)];
            return copy;
          });
        }
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [String(fieldId)]: option
          ? { value: option.value, label: option.label, key: option.key || option.objectKey, type }
          : undefined,
      }));
    },
    clearValue: (fieldId) => {
      setFormData((prev) => {
        const copy = { ...prev };
        delete copy[String(fieldId)];
        return copy;
      });
    },
    fetcher: async (fieldId, selections) => {
      const params = new URLSearchParams();
      Object.entries(selections || {}).forEach(([k, v]) => {
        const label = v?.label || '';
        if (label) params.append(String(k), String(label));
      });
      if (Array.isArray(userSectors) && userSectors.length) params.append('userSectors', userSectors.join(','));
      if (userRole) params.append('userRole', userRole);
      if (userAccountId) params.append('userAccountId', userAccountId);
      if (serviceDeskId) params.append('serviceDeskId', serviceDeskId);
      const qs = params.toString();
      const url = `assets/${encodeURIComponent(fieldId)}${qs ? `?${qs}` : ''}`;
      try { console.log('[FormType] CMDB fetcher', { fieldId, selections, userSectors, userRole, userAccountId, serviceDeskId, qs, url }); } catch (_) {}
      const res = await fetchWithAuth(url);
      const body = await res.json();
      const arr = mapOptions(body);
      try { console.log('[FormType] CMDB fetcher result', { fieldId, count: arr.length }); } catch (_) {}
      return arr;
    },
    userCtx: cmdbUserCtx,
    autoSelectSingle: true,
  });

  useEffect(() => {
    if (!appliedPrefillKey || isPrefilling || !cmdbFields.length) return;

    const firstEmptyFieldId = cmdbFields.find(fieldId => !getCurrentCmdbValue(fieldId));
    if (!firstEmptyFieldId || !cmdb.isFieldEnabled(firstEmptyFieldId)) return;

    const currentSelectionsSignature = cmdbFields
      .map(fieldId => `${fieldId}:${getCurrentCmdbValue(fieldId)?.value || ''}`)
      .join('|');
    const loadSignature = `${appliedPrefillKey}:${firstEmptyFieldId}:${currentSelectionsSignature}`;

    if (prefillChainLoadedRef.current === loadSignature) return;

    prefillChainLoadedRef.current = loadSignature;
    cmdb.loadOptionsFor(firstEmptyFieldId);
  }, [appliedPrefillKey, isPrefilling, cmdbFields, cmdb, getCurrentCmdbValue]);

  // Modal-only fields have no Select to trigger CMDB option loading on menu open,
  // so preload their options as soon as the field becomes enabled.
  useEffect(() => {
    MODAL_ONLY_FIELDS.forEach((fieldId) => {
      if (!cmdbFields.includes(fieldId)) return;
      if (!cmdb.isFieldEnabled(fieldId)) return;
      if (cmdb.optionsByField[fieldId]) return;
      cmdb.loadOptionsFor(fieldId);
    });
  }, [cmdbFields, cmdb]);

  const handleTextInputChange = (fieldId, value) => {
    setFormData(prev => {
      const newFormData = { ...prev };
      newFormData[fieldId] = value;
      return newFormData;
    });
  };

  console.log('[FormType] >>>> formData', formData);

  const handleInputChange = (field, value, label, key) => {
    // console.log('>> handleInputChange', field, value, label);

    if (Array.isArray(value)) {
      setFormData(prev => {
        const newFormData = { ...prev };
        newFormData[field.fieldId] = value;
        return newFormData;
      });

      return;
    }

    if (field.jiraSchema.type === 'option') {
      setFormData(prev => {
        const newFormData = { ...prev };
        newFormData[field.fieldId] = { value, label, type: field?.jiraSchema?.type };
        return newFormData;
      });

      return;
    }

    if (field.jiraSchema.custom !== 'com.atlassian.jira.plugins.cmdb:cmdb-object-cftype' && !field.jiraSchema.custom.includes('elements-connect')) {
      setFormData(prev => {
        const newFormData = { ...prev };
        newFormData[field.fieldId] = value;
        return newFormData;
      });

      return;
    }

    setFormData(prev => {
      const newFormData = { ...prev };
      newFormData[field.fieldId] = { value, label, key, type: field?.jiraSchema?.type };
      return newFormData;
    });
  };

  const openModal = (field, options) => {
    setCurrentField(field);
    setModalOptions(options);
    const currentValue = formData[field.fieldId];
    setModalSelections(
      currentValue
        ? (Array.isArray(currentValue) ? currentValue : [currentValue])
        : []
    );
    setIsModalOpen(true);
  };

  const handleModalSelect = (option) => {
    if (!currentField) return;

    const isMulti = currentField.jiraSchema.custom === 'com.atlassian.jira.plugin.system.customfieldtypes:multiselect'
      || isMultiFields.includes(currentField.fieldId);

    if (!isMulti) {
      handleInputChange(currentField, option.value, option.label, option.key);
      setIsModalOpen(false);
      return;
    }

    setModalSelections(prev => {
      const exists = prev.some(item => item.value === option.value);
      if (exists) {
        return prev.filter(item => item.value !== option.value);
      }
      return [...prev, option];
    });
  };

  const handleModalSubmit = () => {
    handleInputChange(currentField, modalSelections);
    setIsModalOpen(false);
    setModalSelections([]);
  };

  const handleSelectOpen = (fieldId) => {
    if (isMobile) {
      setOpenSelectId(fieldId);
    }
  };

  const handleSelectClose = () => {
    if (isMobile) {
      setOpenSelectId(null);
    }
  };

  // Helper function to find illustration URL for a mobilier
  const getMobilierIllustrationUrl = (mobilierLabel) => {
    if (!mobilierIllustrations.length) return null;

    const illustration = mobilierIllustrations.find(
      item => item.mobilier === mobilierLabel && item.has_illustration
    );

    return illustration ? illustration.illustration_url : null;
  };

  const renderField = (field) => {
    if (field.visible === false) return null;

    switch (field?.jiraSchema?.type) {
      case 'date':
        return (
          <div key={field.fieldId} className="mb-6">
            <label
              htmlFor={field.fieldId}
              className="block text-sm font-medium text-gray-900 mb-2"
            >
              {field.name} {field.required && <span className="text-red-600">*</span>}
            </label>
            <input
              type="date"
              id={field.fieldId}
              required={field.required}
              className="mt-1 p-3 block w-full rounded-lg border-2 border-gray-300 shadow-sm focus:border-black focus:ring-2 focus:ring-black transition-colors text-sm"
              onChange={(e) => handleTextInputChange(field.fieldId, e.target.value)}
              value={formData[field.fieldId] || ''}
            />
          </div>
        );

      case 'number':
        return (
          <div key={field.fieldId} className="mb-6">
            <label
              htmlFor={field.fieldId}
              className="block text-sm font-medium text-gray-900 mb-2"
            >
              {field.name} {field.required && <span className="text-red-600">*</span>}
            </label>
            <input
              type="number"
              id={field.fieldId}
              required={field.required}
              className="mt-1 p-3 block w-full rounded-lg border-2 border-gray-300 shadow-sm focus:border-black focus:ring-2 focus:ring-black transition-colors text-sm placeholder-gray-400"
              onChange={(e) => {
                const raw = e.target.value;
                handleTextInputChange(field.fieldId, raw === '' ? '' : Number(raw));
              }}
              value={formData[field.fieldId] ?? ''}
              placeholder={t('Enter {{field}}…', { field: field.name.toLowerCase() })}
            />
          </div>
        );

      case 'string':
        const isTextArea = field.jiraSchema.custom === 'com.atlassian.jira.plugin.system.customfieldtypes:textarea' || field.fieldId === 'description';

        return (
          <div key={field.fieldId} className="mb-6">
            <label
              htmlFor={field.fieldId}
              className="block text-sm font-medium text-gray-900 mb-2"
            >
              {field.name} {field.required && <span className="text-red-600">*</span>}
            </label>
            {isTextArea ? <textarea
              id={field.fieldId}
              required={field.required}
              className="mt-1 p-3 block w-full rounded-lg border-2 border-gray-300 shadow-sm focus:border-black focus:ring-2 focus:ring-black transition-colors text-sm placeholder-gray-400"
              onChange={(e) => handleTextInputChange(field.fieldId, e.target.value)}
              value={formData[field.fieldId] || ''}
              rows={4}
              placeholder={t('Enter {{field}}…', { field: field.name.toLowerCase() })}
            /> : <input
              type="text"
              id={field.fieldId}
              required={field.required}
              className="mt-1 p-3 block w-full rounded-lg border-2 border-gray-300 shadow-sm focus:border-black focus:ring-2 focus:ring-black transition-colors text-sm placeholder-gray-400"
              onChange={(e) => handleTextInputChange(field.fieldId, e.target.value)}
              value={formData[field.fieldId] || ''}
              placeholder={t('Enter {{field}}…', { field: field.name.toLowerCase() })}
            />
            }
          </div>
        );

      case 'option':
      case 'array':
      case 'object':
        // Handle attachments
        if (field.jiraSchema.system === 'attachment') {
          const MAX_FILES = 4;
          const currentFilesCount = formData[field.fieldId]?.length || 0;
          const remainingSlots = MAX_FILES - currentFilesCount;
          const isLimitReached = currentFilesCount >= MAX_FILES;

          return (
            <div key={field.fieldId} className="mb-4">
              <label
                htmlFor={field.fieldId}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {field.name} {field.required && <span className="text-red-500">*</span>}
              </label>

              {/* File Input Button */}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <label
                  htmlFor={`${field.fieldId}-input-camera`}
                  className={`cursor-pointer inline-flex items-center px-5 py-3 border-2 border-gray-300
                    rounded-lg text-sm font-medium text-gray-700 bg-white
                    hover:bg-gray-50 hover:border-gray-400 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black
                    ${isLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <CameraIcon className="w-5 h-5 mr-2 text-gray-600" />
                  {t('Take a photo')}
                </label>
                <input
                  type="file"
                  id={`${field.fieldId}-input-camera`}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  capture="environment"
                  className="hidden"
                  disabled={isLimitReached}
                  onChange={e => onSelectFilesChange(field, e)}
                />

                <label
                  htmlFor={`${field.fieldId}-input-gallery`}
                  className={`cursor-pointer inline-flex items-center px-5 py-3 border-2 border-gray-300
                    rounded-lg text-sm font-medium text-gray-700 bg-white
                    hover:bg-gray-50 hover:border-gray-400 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black
                    ${isLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <ImageIcon className="w-5 h-5 mr-2 text-gray-600" />
                  {t('Add from gallery')}
                </label>
                <input
                  type="file"
                  id={`${field.fieldId}-input-gallery`}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  multiple
                  className="hidden"
                  disabled={isLimitReached}
                  onChange={e => onSelectFilesChange(field, e)}
                />
              </div>

              {/* Files Preview Grid */}
              {formData[field.fieldId]?.length > 0 && (
                <div className="mt-4">
                  <AttachmentsGallery
                    attachments={formData[field.fieldId].map(file => ({
                      filename: file.file.name,
                      url: URL.createObjectURL(file.file),
                      thumbnail: file.thumbnail || null,
                      isUploading: file.isUploading,
                      uploadFailed: file.uploadFailed
                    }))}
                    editable={true}
                    onDelete={(attachment, index) => {
                      const fileToRemove = formData[field.fieldId][index];
                      const newFiles = formData[field.fieldId].filter((_, i) => i !== index);
                      handleInputChange(field, newFiles);

                      if (fileToRemove?.id) {
                        const newAttachments = (formData['_attachments'] || []).filter(
                          att => att.id !== fileToRemove.id
                        );
                        handleInputChange({ fieldId: '_attachments' }, newAttachments);
                      }
                    }}
                  />
                </div>
              )}
              <div className="mt-3">
                <UploadProgressIndicator
                  uploadCount={formData[field.fieldId]?.filter(f => f.isUploading).length || 0}
                />
              </div>
            </div>
          );
        }

        // Handle simple radiobuttons (non-CMDB, simple option field)
        const isRadioButtons = field?.jiraSchema?.custom?.includes('radiobuttons');

        if (isRadioButtons) {
          const options = field.validValues
            ? field.validValues.map(({ value, label }) => ({
                value,
                label,
              }))
            : [];

          const currentValue = formData[field.fieldId];
          const selectedValue =
            currentValue && typeof currentValue === 'object'
              ? currentValue.value
              : currentValue;

          return (
            <div key={field.fieldId} className="mb-4">
              <label
                htmlFor={field.fieldId}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {field.name}{' '}
                {field.required && <span className="text-red-500">*</span>}
              </label>

              <div className="mt-2 flex flex-col gap-2">
                {options.map((option) => (
                  <label
                    key={option.value}
                    className="inline-flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="radio"
                      name={field.fieldId}
                      value={option.value}
                      checked={selectedValue === option.value}
                      onChange={() =>
                        handleInputChange(
                          field,
                          option.value,
                          option.label,
                          option.key
                        )
                      }
                      required={field.required}
                      className="h-4 w-4 text-black border-gray-300 focus:ring-black"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        }

        let options = [];
        let isCmdb = false;
        let isMulti = field.jiraSchema.custom === 'com.atlassian.jira.plugin.system.customfieldtypes:multiselect';

        // fucking hack for grid cmdb item
        if (isMultiFields.includes(field.fieldId))
          isMulti = true;

        if (field.jiraSchema.custom === 'com.atlassian.jira.plugins.cmdb:cmdb-object-cftype' || field.jiraSchema.custom.includes('elements-connect')) {
          options = cmdb.optionsByField[field.fieldId] || [];
          isCmdb = true;
        } else if (field.validValues) {
          options = field.validValues.map(({ value, label }) => ({
            value,
            label
          }));
        }

        const value = (() => {
          if (isCmdb || field.jiraSchema.type === 'option') {
            if (isMulti)
              return formData[field.fieldId] || [];

            const data = options.find(option =>
              option.value === (formData[field.fieldId]?.value || formData[field.fieldId])
            ) || null;

            if (!data)
              return formData[field.fieldId];

            return data;
          }

          return isMulti ? formData[field.fieldId] : formData[field.fieldId]?.value;
        })();

        const hasValue = isMulti
          ? Array.isArray(value) && value.length > 0
          : Boolean(value && (value.value || value.label || typeof value === 'string'));

        // Check if it's a CMDB field with only one option
        const hasOnlyOneOption = isCmdb && options.length === 1;
        const hasNoOption = isCmdb && options.length === 0 && !hasValue;

        // Client-specific: hide the react-select entirely and force selection
        // through the picture modal via a call-to-action button.
        const isModalOnly = MODAL_ONLY_FIELDS.includes(field.fieldId);
        const isModalOnlyDisabled = isModalOnly && isCmdb && (!cmdb.isFieldEnabled(field.fieldId) || hasNoOption);

        // When the catalog button is disabled, explain what still needs to be
        // selected above so it becomes active.
        const modalOnlyDisabledHint = (() => {
          if (!isModalOnlyDisabled) return null;

          if (!cmdb.isFieldEnabled(field.fieldId)) {
            const idx = cmdbFields.indexOf(field.fieldId);
            const missingFieldId = cmdbFields
              .slice(0, idx)
              .find((prevId) => !getCurrentCmdbValue(prevId));
            const missingField = formTypeFields.find((f) => f.fieldId === missingFieldId);
            const missingName = missingField?.name;

            return missingName
              ? `Sélectionnez d'abord « ${missingName} » ci-dessus pour accéder au catalogue.`
              : 'Renseignez les champs précédents pour accéder au catalogue.';
          }

          return 'Aucun élément disponible pour cette sélection.';
        })();

        // LOREAL HACK- INJECT SAP CODE
        if (serviceDeskConfig?.interventionPointConfig?.attributes?.sap) {
          const sapCodeId = serviceDeskConfig.interventionPointConfig.attributes.sap.id;

          if (sapCodeId) {
            options = options.map(option => {
              const sapCode = option.attributes?.find(attribute => attribute.id === `${sapCodeId}`)?.objectAttributeValues?.[0]?.displayValue;

              if (sapCode) {
                return { ...option, enhancedLabel: `${sapCode} ${option.label}` };
              }

              return option;
            });
          }
        }

        return (
          <div key={field.fieldId} className={`mb-4 ${field.fieldId} ${field.name}`}>
            <label
              htmlFor={field.fieldId}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {field.name} {field.required && <span className="text-red-500">*</span>}
            </label>

            <div className="flex flex-col gap-2">
              <div className="flex flex-row gap-2">
                {isModalOnly ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (isCmdb && cmdb.isFieldEnabled(field.fieldId)) {
                        cmdb.loadOptionsFor(field.fieldId);
                      }
                      openModal(field, options);
                    }}
                    disabled={isModalOnlyDisabled}
                    className="mt-1 inline-flex items-center gap-2 px-5 py-3 border-2 border-gray-300
                      rounded-lg text-sm font-medium text-gray-700 bg-white
                      hover:bg-gray-50 hover:border-gray-400 transition-all focus:outline-none
                      focus:ring-2 focus:ring-offset-2 focus:ring-black
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ImageIcon className="w-5 h-5 text-gray-600" />
                    {hasValue ? t('Edit selection') : t('Select from the catalog')}
                  </button>
                ) : (
                <>
                <Select
                  id={field.fieldId}
                  options={options}
                  getOptionLabel={(option) =>
                    appendStockToOptionLabel(option.enhancedLabel || option.label, option)
                  }
                  isRequired={field.required}
                  isClearable
                  isSearchable
                  isMulti={isMulti}
                  isLoading={isCmdb && cmdb.loadingField === field.fieldId}
                  isDisabled={isCmdb ? (!cmdb.isFieldEnabled(field.fieldId) || hasNoOption) : (hasOnlyOneOption || hasNoOption)}
                  placeholder={t('Select an option…')}
                  noOptionsMessage={() => t('No option')}
                  onChange={(option) => {
                    if (isMulti) {
                      // Store full multi selection in form state
                      handleInputChange(field, option);

                      // For CMDB multi fields, still drive the chain by simulating a single selection
                      if (isCmdb) {
                        const first = Array.isArray(option) ? (option[0] || null) : option;
                        try {
                          console.log('[FormType] onChange CMDB multi', {
                            fieldId: field.fieldId,
                            count: Array.isArray(option) ? option.length : 0,
                            first,
                          });
                        } catch (_) {}
                        cmdb.handleSelect(field.fieldId, first);
                      }
                      return;
                    }
                    if (isCmdb) {
                      try { console.log('[FormType] onChange CMDB', { fieldId: field.fieldId, option }); } catch (_) {}
                      cmdb.handleSelect(field.fieldId, option);
                      return;
                    }
                    // Non-CMDB: support clearing as well (option can be null)
                    handleInputChange(field, option?.value, option?.label, option?.key);
                  }}
                  value={value}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  onMenuOpen={() => {
                    handleSelectOpen(field.fieldId);
                    if (isCmdb) {
                      if (!cmdb.isFieldEnabled(field.fieldId)) return;
                      cmdb.loadOptionsFor(field.fieldId);
                    }
                  }}
                  onMenuClose={handleSelectClose}
                  menuPortalTarget={isMobile ? null : document.body}
                  menuPosition="absolute"
                  styles={{
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999
                    }),
                    control: (base) => ({
                      ...base,
                      minHeight: '30px',
                      height: 'auto',
                      backgroundColor: (isCmdb ? hasNoOption : (hasOnlyOneOption || hasNoOption)) ? '#f3f4f6' : base.backgroundColor
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      height: 'auto',
                      padding: '0 6px',
                      flexWrap: 'wrap'
                    }),
                    multiValue: (base) => ({
                      ...base,
                      width: 'auto',
                      maxWidth: '260px',
                      margin: '2px',
                    }),
                    input: (base) => ({
                      ...base,
                      margin: '0px'
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: '30px'
                    }),
                  }}
                  classNamePrefix="select"
                />

                {(itemsWithPictures.includes(field.fieldId) && field.fieldId !== 'customfield_10098') && (
                  <button
                    type="button"
                    onClick={() => options?.length && openModal(field, options)}
                    className="mt-1 ml-2 block rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm flex items-center justify-center"
                  >
                    <svg className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 40 40" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>
                )}
                </>
                )}
              </div>

              {modalOnlyDisabledHint && (
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <InfoCircledIcon className="w-3 h-3 flex-shrink-0" />
                  {modalOnlyDisabledHint}
                </p>
              )}

              {/* Add preview section for both customfield_10102 and customfield_10098 */}
              {itemsWithPictures.includes(field.fieldId) && value && (
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {(Array.isArray(value)
                    ? value.length > 0 ? value : []
                    : (value && (value.value || value.label)) ? [value] : []
                  ).map((item) => {
                    // Skip if item is null or undefined (empty selection)
                    if (!item || (!item.value && !item.label)) return null;

                    // For customfield_10098 (mobilier), use the mobilier illustration
                    const illustrationUrl = field.fieldId === 'customfield_10098'
                      ? getMobilierIllustrationUrl(item.label)
                      : null;

                    return (
                      <div key={item.value} className="relative flex flex-col items-center h-full">
                        <div className="w-[150px] h-[150px] flex items-center justify-center">
                          {field.fieldId === 'customfield_10098' && illustrationUrl ? (
                            // Display mobilier illustration if available
                            <img
                              src={illustrationUrl}
                              alt={item.label}
                              className="max-w-full max-h-full object-contain"
                            />
                          ) : (
                            // Otherwise use the Picture component (for grids)
                            <Picture
                              label={item.label}
                              pictures={picturesData}
                              url={getOptionImageUrl(item)}
                              className="max-w-full max-h-full object-contain"
                            />
                          )}
                        </div>
                        {hasQuantity.includes(field.fieldId) && (
                          <>
                            <div className="mt-2">
                              <ItemQuantity
                                item={item}
                                quantity={item.quantity}
                                onChange={(quantity) => handleItemQuantityChange(field.fieldId, item, quantity)}
                              />
                            </div>
                            <div className="text-sm text-gray-600 truncate max-w-[150px]">{item.label}</div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Attributes preview for CMDB fields configured in fieldsWithAttributes (compact style) */}
              {fieldsWithAttributes.includes(field.fieldId) && fieldsAttributes?.[field.fieldId] && detailAttributes.length > 0 && (
                <div className="mt-3">
                  <InterventionPointsDetail
                    detail={fieldsAttributes[field.fieldId]?.attributes}
                    orderList={detailAttributes}
                  />
                </div>
              )}
            </div>
          </div>
        )

      default:
        return null;
    }
  };

  const renderFields = () => {
    // Create a copy of the fields to avoid mutating the original
    const sortedFields = [...formTypeFields];

    // Find the index of customfield_10102
    const specialFieldIndex = sortedFields.findIndex(field => field.fieldId === 'customfield_10102');

    // If the special field exists, remove it from its current position and add it to the end
    if (specialFieldIndex !== -1) {
      const specialField = sortedFields.splice(specialFieldIndex, 1)[0];
      sortedFields.push(specialField);
    }

    // Render all fields in the new order
    return sortedFields.map(field => {
      return (
        <div key={field.fieldId}>
          {renderField(field)}

          {field.description ? (
            <p className="mt-1 text-sm text-gray-500 flex items-center gap-1">
              <InfoCircledIcon className="w-3 h-3" />
              {field.description}
            </p>
          ) : null}
        </div>);
    });
  };

  // get attributes for fieldsWithAttributes on change for specific fields
  useEffect(() => {
    if (!formData) return;

    const getObjectKey = (fieldId) => {
      if (formData[fieldId].key) return formData[fieldId].key;
      return formData[fieldId].value.split(':')[1];
    }

    const fetchAttributes = async (fieldId) => {
      console.log('[fieldsWithAttributes] fetching attributes for field', fieldId);

      try {
        const objectKey = getObjectKey(fieldId);
        const response = await fetchWithAuth(`/intervention-points/${objectKey}/attributes`);
        const { data } = await response.json();
        console.log('[FormType] attributes', data);
        setFieldsAttributes(prev => ({ ...prev, [fieldId]: { objectKey, attributes: data } }));
      } catch (error) {
        console.error('Error fetching attributes:', error);
      }
    }

    fieldsWithAttributes.forEach(fieldId => {

      // do not fetch if no value for the field in formData
      // clear fetched attributes for the field if no value
      if (!formData[fieldId]) {
        if (fieldsAttributes[fieldId]) {
          setFieldsAttributes(prev => ({ ...prev, [fieldId]: false }));
        }

        return;
      }

      // do not fetch attributes if already fetched for the same object key
      if (fieldsAttributes[fieldId]?.objectKey === getObjectKey(fieldId)){
        console.log('[fieldsWithAttributes] already fetched for the same object key', fieldsAttributes);
        return;
      }

      fetchAttributes(fieldId);
    });

  }, [formData]);

  // Log when a single option is detected and expected to be auto-validated by the hook
  useEffect(() => {
    try {
      cmdbFields.forEach((fid) => {
        const opts = cmdb.optionsByField?.[fid] || [];
        const selected = formData?.[fid];
        const enabled = cmdb.isFieldEnabled(fid);
        if (enabled && opts.length === 1 && !selected) {
          console.log('[FormType] single option detected; hook will auto-validate like a user click', {
            fieldId: fid,
            option: opts[0],
          });
        }
      });
    } catch (_) {}
  }, [cmdb.optionsByField, cmdbFields, formData, cmdb]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const body = {
      ...formData,
      attachments: formData['_attachments'],
      requestTypeId: selectedFormType,
      raiseOnBehalfOf: userAccountId,
    };

    if (serviceDeskId) {
      body.serviceDeskId = serviceDeskId;
    }

    const errors = [];

    formTypeFields.forEach(field => {
      if (field.required && !body[field.fieldId]) {
        errors.push(t('{{field}} is required', { field: field.name }));
      }
    });

    if (errors.length) {
      setErrors(errors);
      return;
    }

    delete body.attachment;
    delete body._attachments;

    setIsLoading(true);
    const startTime = Date.now();

    try {
      const response = await fetchWithAuth(`request`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        let errorMessage;

        try {
          errorMessage = await response.json();
        } catch (_) {
          errorMessage = await response.text();
        }

        setError(errorMessage);

        throw new Error();
      }

      const data = await response.json();

      // Ensure loading state stays for at least 4 seconds
      const elapsedTime = Date.now() - startTime;

      if (elapsedTime < 4000) {
        await new Promise(resolve => setTimeout(resolve, 4000 - elapsedTime));
      }

      // if 2 steps creation, ensure we wait 2 more seconds before navigating to the issue
      try {
        if (serviceDeskConfig?.twoStepsCreation?.includes(selectedFormType)) {
          await new Promise(resolve => setTimeout(resolve, 2000));

          appNavigate('issues', { issueKey: data.issueKey });
          navigate(`/issues/${encodeURIComponent(data.issueKey)}`);
          return data;
        }
      } catch (_) {}

      setIsSuccess(true);
      setIsLoading(false);

      return data;
    } catch (error) {
      setIsLoading(false);
    }
  };

  const uploadFile = async (file, id) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const url = serviceDeskId
        ? `attachments/upload?serviceDeskId=${serviceDeskId}`
        : `attachments/upload`;

      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: formData
      });

      if (!response.ok)
        throw new Error('Upload failed');

      const data = await response.json();

      return {
        ...data[0],
        id
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleItemQuantityChange = (fieldId, targetItem, quantity) => {
    setFormData(prev => {
      const currentItems = [...prev[fieldId]];
      const updatedItems = currentItems.map(item =>
        item.value === targetItem.value ? { ...item, quantity } : item
      );
      return {
        ...prev,
        [fieldId]: updatedItems
      };
    });
  };

  return (
    <div className="h-full w-full max-w-5xl mx-auto bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm">
      {isLoading && <LoadingModal />}
      {isPrefilling && <LoadingModal customText={t('Prefilling the request…')} />}

      {error?.errorCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-red-900">{t('An error occurred.')}</h3>
              <button
                onClick={() => setError(null)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label={t('Close')}
              >
                <Cross2Icon className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <p className="text-sm text-red-700">{getErrorMessage(t, error?.errorCode)}</p>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-600">
                {t('Please send a screenshot of this error for resolution')}
              </p>
            </div>
          </div>
        </div>
      )}

      {isSuccess && (
        <div className="flex flex-col items-center justify-center py-12">
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={200}
            tweenDuration={5000}
          />
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
            <CheckCircledIcon className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">{t('Request created successfully!')}</h1>
          <p className="text-base text-gray-600 text-center max-w-md leading-relaxed">
            {t('Your request has been created. You can now track its progress from your requests list.')}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <button
              onClick={() => {
                appNavigate('issues');
                navigate('/issues');
              }}
              className="flex-1 px-6 py-3 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
            >
              {t('View my requests')}
            </button>

            <button
              onClick={() => {
                onCancel();
              }}
              className="flex-1 px-6 py-3 bg-white text-black font-medium rounded-lg border-2 border-black hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
            >
              {t('Create a new request')}
            </button>
          </div>

        </div>
      )}

      {!isSuccess && !isLoading && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {renderFields()}

          {hasQuota && !hasRole(userRole, 'technician+') && (
            <QuotaPanel
              enabled={hasQuota}
              serviceDeskId={serviceDesk?.id}
              requestTypeId={Number(selectedFormType)}
              requiredFieldIds={requiredFieldIds}
              formData={formData}
              onQuotaStateChange={setQuotaState}
            />
          )}


          {errors.length > 0 && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Cross2Icon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-900 mb-1">{t('Validation errors')}</h3>
                  <ul className="text-sm text-red-700 space-y-1">
                    {errors.map(error => <li key={error}>• {error}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              onClick={onCancel}
              type="button"
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              disabled={uploadingFiles.size > 0}
              className={`px-6 py-3 text-sm font-medium border-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                uploadingFiles.size > 0
                  ? 'bg-gray-400 text-gray-200 border-gray-400 cursor-not-allowed'
                  : 'bg-black text-white border-black hover:bg-gray-800 hover:border-gray-800 focus:ring-black'
              }`}
            >
             {uploadingFiles.size > 0
               ? t('Uploading ({{count}})…', { count: uploadingFiles.size })
               : serviceDeskConfig?.twoStepsCreation?.includes(selectedFormType) ? t('Next') : t('Send the request')
             }
            </button>
          </div>
        </form>
      )}

      {isModalOpen && (
        <PictureSelectionModal
          isOpen={true}
          onClose={() => {
            setIsModalOpen(false);
            setModalSelections([]);
          }}
          currentField={currentField}
          options={modalOptions}
          selections={modalSelections}
          onSelect={handleModalSelect}
          onSubmit={handleModalSubmit}
          picturesData={picturesData}
          multiFields={isMultiFields}
          requestTypeName={selectedFormTypeName}
          serviceDeskId={serviceDeskId}
        />
      )}

      {/* Show loading modal while processing QR code */}
      {isScanning && (
        <LoadingModal customText={t('Processing the QR code…')} />
      )}
    </div>
  );
}

export default FormType;
